import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { LoadingDots } from '@shared/atoms';
import React, { FunctionComponent } from 'react';
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';
import { RootState } from 'store';

export enum ProtectedTypes {
  JOIN = 'Join',
  REGISTERED = 'Registered',
  AGREE_TERMS = 'Agree_Terms',
  ON_BOARDING_ASSESSMENT = 'On_Boarding_Assessment',
}

const ProtectedRoute: React.FunctionComponent<ProtectedRouteProps> = ({
  component: Component,
  RedirectFunction,
  authorizedRoles = [],
  condition,
  ...rest
}) => {
  const isAuthenticated = Boolean(localStorage.getItem('token'));
  const version = localStorage.getItem('v');
  const location = useLocation();
  const user = localStorage.getItem('user');
  const parsedUser = JSON.parse(user || '{}');
  const parsedUserRole = parsedUser.role ? parsedUser.role : null;
  const { isDesktop } = useWindowDimensions();

  const isRegistered = Boolean(
    useAppSelector((state: RootState) => state.userStatus.data.isRegistered),
  );
  const isJoined = Boolean(useAppSelector((state: RootState) => state.userStatus.data.isJoined));
  const isAgreed = Boolean(useAppSelector((state: RootState) => state.userStatus.data.isAgreed));
  const statusLoaded = Boolean(useAppSelector((state: RootState) => state.userStatus.isLoaded));
  const statusIsLoading = Boolean(useAppSelector((state: RootState) => state.userStatus.loading));

  const companyConfigsLoaded = Boolean(
    useAppSelector((state: RootState) => state.companyConfig.isLoaded),
  );

  const isOnboardingCompleted = Boolean(
    useAppSelector((state: RootState) => state.userStatus.data.isOnboardCompleted),
  );

  const locationChecks = '/login';

  // Authentication check
  if (!isAuthenticated || version !== '2.0') {
    if (location.pathname !== locationChecks) {
      localStorage.setItem('redirectPath', location.pathname + location.search);
    }

    return <Redirect to="/login" />;
  }

  // Role authorization check
  if (authorizedRoles.length > 0 && !authorizedRoles.includes(parsedUserRole)) {
    return <Redirect to="/404" />;
  }

  // Custom condition check
  if (statusLoaded && companyConfigsLoaded && condition !== undefined && !condition) {
    return <Redirect to="/404" />;
  }

  // Wait for status to load before making redirect decisions
  if (!statusLoaded || !companyConfigsLoaded || statusIsLoading) {
    <div className="initialLoadingContainer">
      <LoadingDots size={isDesktop ? 24 : 20} isHapstarLoading dotCount={3} />
    </div>;
  }

  // Global onboarding check - redirect to onboarding if user needs to complete it
  // Only skip this check if we're already on the onboarding assessment page or on prerequisite pages
  const prerequisitePages = [
    '/complete-signup',
    '/welcome',
    '/introduction',
    '/onboarding-assessment',
  ];

  if (
    isRegistered &&
    isAgreed &&
    isJoined &&
    !isOnboardingCompleted &&
    statusLoaded &&
    !statusIsLoading &&
    !prerequisitePages.includes(location.pathname) &&
    !location.pathname.includes('/employer')
  ) {
    return <Redirect to="/onboarding-assessment" />;
  }

  switch (RedirectFunction) {
    case ProtectedTypes.JOIN:
      if (!isRegistered) {
        return <Redirect to="/complete-signup" />;
      }

      if (!isAgreed) {
        return <Redirect to="/welcome" />;
      }

      if (isJoined) {
        return <Redirect to="/" />;
      }

      return <Route {...rest} render={(props: any) => <Component {...props} />} />;

    case ProtectedTypes.AGREE_TERMS:
      if (!isRegistered) {
        return <Redirect to="/complete-signup" />;
      }

      if (isAgreed) {
        return <Redirect to="/introduction" />;
      }

      return <Route {...rest} render={(props: any) => <Component {...props} />} />;

    case ProtectedTypes.REGISTERED:
      if (isRegistered) {
        return <Redirect to="/welcome" />;
      }

      return <Route {...rest} render={(props: any) => <Component {...props} />} />;

    case ProtectedTypes.ON_BOARDING_ASSESSMENT:
      // Check if user meets prerequisites for onboarding assessment
      if (!isRegistered) {
        return <Redirect to="/complete-signup" />;
      }

      if (!isAgreed) {
        return <Redirect to="/welcome" />;
      }

      if (!isJoined) {
        return <Redirect to="/introduction" />;
      }

      // If onboarding is already completed, redirect to home
      if (isOnboardingCompleted) {
        return <Redirect to="/" />;
      }

      // Allow access to onboarding assessment page
      return <Route {...rest} render={(props: any) => <Component {...props} />} />;

    default:
      if (!isRegistered) {
        return <Redirect to="/complete-signup" />;
      }

      if (!isAgreed) {
        return <Redirect to="/welcome" />;
      }

      if (!isJoined) {
        return <Redirect to="/introduction" />;
      }

      // This is handled by the global onboarding check above
      return <Route {...rest} render={(props: any) => <Component {...props} />} />;
  }
};

ProtectedRoute.defaultProps = {
  RedirectFunction: 'default',
  authorizedRoles: [],
  condition: undefined,
};

interface ProtectedRouteProps extends RouteProps {
  component: FunctionComponent;
  RedirectFunction?: any;
  authorizedRoles?: string[];
  condition?: boolean;
}

export default ProtectedRoute;
